import { useEffect, useRef } from "react";

import { Input } from "@lawline/ui/components/input";

// Inline name editor for both renaming and creating. Enter commits a non-empty
// trimmed value; Escape or a pointer-down outside the field cancels. The `done`
// guard keeps commit/cancel from firing twice.
export function NameInput({
	defaultValue,
	onCommit,
	onCancel,
}: {
	defaultValue: string;
	onCommit: (value: string) => void;
	onCancel: () => void;
}) {
	const ref = useRef<HTMLInputElement>(null);
	const done = useRef(false);

	const commit = (raw: string) => {
		if (done.current) return;
		done.current = true;
		const value = raw.trim();
		value ? onCommit(value) : onCancel();
	};
	const cancel = () => {
		if (done.current) return;
		done.current = true;
		onCancel();
	};

	useEffect(() => {
		// Cancel on a real pointer-down outside the field, not on blur. The click
		// that opened the field fired its pointerdown in the previous task, so this
		// handler never catches it. Only a genuine click-away cancels.
		const onPointerDown = (e: PointerEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) cancel();
		};
		// Re-assert focus for a few frames. The menu that opened the field restores
		// focus asynchronously and can steal the selection on a stray frame.
		// Skip when the field already holds focus, so a real caret stays intact.
		let frames = 0;
		let raf = requestAnimationFrame(function grab() {
			const el = ref.current;
			if (el && document.activeElement !== el) {
				el.focus();
				el.select();
			}
			if (frames === 0)
				document.addEventListener("pointerdown", onPointerDown, true);
			if (++frames < 4) raf = requestAnimationFrame(grab);
		});
		return () => {
			cancelAnimationFrame(raf);
			document.removeEventListener("pointerdown", onPointerDown, true);
		};
	}, []);

	return (
		<Input
			ref={ref}
			defaultValue={defaultValue}
			className="h-6 px-1 py-0 text-center text-xs"
			onClick={(e) => e.stopPropagation()}
			onDoubleClick={(e) => e.stopPropagation()}
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					e.stopPropagation();
					commit(e.currentTarget.value);
				}
				if (e.key === "Escape") {
					e.stopPropagation();
					cancel();
				}
			}}
		/>
	);
}
